import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, DollarSign, TrendingUp, AlertCircle } from "lucide-react";
import { calculatePoolImpactOnTitleIX } from "@/services/titleIXService";

export default function RevenueSharingPools() {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [titleIXImpact, setTitleIXImpact] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    sport: "",
    gender: "male",
    academic_year: new Date().getFullYear().toString(),
    total_pool_amount: "",
    notes: "",
  });

  const { data: pools, isLoading } = useQuery({
    queryKey: ["revenue_sharing_pools"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("revenue_sharing_pools" as any)
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const addPoolMutation = useMutation({
    mutationFn: async (pool: typeof formData) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from("revenue_sharing_pools" as any)
        .insert([{
          ...pool,
          total_pool_amount: parseFloat(pool.total_pool_amount),
          created_by: user?.id,
        }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["revenue_sharing_pools"] });
      toast.success("Revenue sharing pool created");
      setIsAddDialogOpen(false);
      setTitleIXImpact(null);
      setFormData({
        sport: "",
        gender: "male",
        academic_year: new Date().getFullYear().toString(),
        total_pool_amount: "",
        notes: "",
      });
    },
    onError: (error) => {
      toast.error("Failed to create pool: " + error.message);
    },
  });

  const handleCheckTitleIXImpact = async () => {
    if (!formData.total_pool_amount || !formData.gender || !formData.academic_year) {
      toast.error("Please fill in amount, gender, and academic year");
      return;
    }

    try {
      const result = await calculatePoolImpactOnTitleIX(
        formData.gender as "male" | "female" | "coed",
        parseFloat(formData.total_pool_amount),
        formData.academic_year
      );
      setTitleIXImpact(result.impactSummary);
    } catch (error) {
      toast.error("Failed to calculate Title IX impact");
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getUtilizationPercentage = (allocated: number, total: number) => {
    return total > 0 ? (allocated / total) * 100 : 0;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Revenue Sharing Pools</h1>
            <p className="text-muted-foreground">
              Manage CAPS revenue sharing pools by sport and gender
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Pool
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Revenue Sharing Pool</DialogTitle>
                <DialogDescription>
                  Create a new revenue sharing pool for athlete compensation
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sport">Sport *</Label>
                    <Input
                      id="sport"
                      value={formData.sport}
                      onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                      placeholder="e.g., Football, Basketball"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="coed">Coed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="academic_year">Academic Year *</Label>
                    <Input
                      id="academic_year"
                      value={formData.academic_year}
                      onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                      placeholder="2024-2025"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_pool_amount">Total Pool Amount *</Label>
                    <Input
                      id="total_pool_amount"
                      type="number"
                      value={formData.total_pool_amount}
                      onChange={(e) => setFormData({ ...formData, total_pool_amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional information about this pool"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCheckTitleIXImpact}
                  >
                    <AlertCircle className="mr-2 h-4 w-4" />
                    Check Title IX Impact
                  </Button>
                  {titleIXImpact && (
                    <Badge variant={titleIXImpact.includes("✓") ? "default" : "destructive"}>
                      {titleIXImpact}
                    </Badge>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => addPoolMutation.mutate(formData)}
                  disabled={!formData.sport || !formData.total_pool_amount || addPoolMutation.isPending}
                >
                  Create Pool
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid gap-4">
            {pools?.map((pool: any) => {
              const utilizationPct = getUtilizationPercentage(
                pool.allocated_amount,
                pool.total_pool_amount
              );

              return (
                <Card key={pool.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        <CardTitle>{pool.sport} - {pool.gender}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{pool.academic_year}</Badge>
                        <Badge variant={pool.status === "active" ? "default" : "secondary"}>
                          {pool.status}
                        </Badge>
                      </div>
                    </div>
                    <CardDescription>
                      {formatCurrency(pool.total_pool_amount)} total pool
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Allocated</span>
                          <span className="font-medium">
                            {formatCurrency(pool.allocated_amount)} ({utilizationPct.toFixed(1)}%)
                          </span>
                        </div>
                        <Progress value={utilizationPct} />
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Remaining:</span>
                          <span className="font-medium ml-2">
                            {formatCurrency(pool.total_pool_amount - pool.allocated_amount)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>
                          <span className="font-medium ml-2 capitalize">{pool.status}</span>
                        </div>
                      </div>

                      {pool.notes && (
                        <p className="text-sm text-muted-foreground">{pool.notes}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {pools?.length === 0 && (
              <Card>
                <CardContent className="text-center py-8 text-muted-foreground">
                  No revenue sharing pools found. Create one to get started.
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
